import React, { useState, useEffect } from 'react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { contactLabel } from '../PrestationPlanning/prestationPlanningShared';
import {
    Search,
    Users,
    Clock,
    AlertCircle,
    Building2,
    User
} from 'lucide-react';
import { motion } from 'motion/react';

interface Employee { id: string; first_name: string; last_name: string; }

const employeeNamesLabel = (appt: any, employees: Employee[]) =>
    (appt.employee_ids || [])
        .map((id: string) => {
            const e = employees.find((x) => x.id === id);
            return e ? `${e.first_name} ${e.last_name}`.trim() : '';
        })
        .filter(Boolean)
        .join(', ');

const PrestationUpcomingTable = () => {
    const [appointments, setAppointments] = useState<any[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [rows, emps] = await Promise.all([
                    moduleApi.getUpcomingPrestationAppointments(20),
                    moduleApi.getEmployes(true), // noms des salariés, y compris partis, sur les rendez-vous
                ]);
                setAppointments(Array.isArray(rows) ? rows : []);
                setEmployees((Array.isArray(emps) ? emps : []).filter((e: any) => !e.deleted_at));
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filteredAppointments = appointments.filter((appt) =>
        contactLabel(appt).toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">Rendez-vous à venir</div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted pointer-events-none" size={15} />
                    <input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="field-input w-full sm:w-64"
                        style={{ paddingLeft: 34 }}
                    />
                </div>
            </div>

            <div className="bg-surface border border-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-surface-soft border-b border-border">
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Date</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Contact</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Prestation</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Horaires</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Salarié(s) assigné(s)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-[var(--text-muted)]">Chargement...</td>
                                </tr>
                            ) : filteredAppointments.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-[var(--text-muted)]">Aucun rendez-vous à venir</td>
                                </tr>
                            ) : filteredAppointments.map((appt) => {
                                const assigned = employeeNamesLabel(appt, employees);
                                return (
                                    <motion.tr
                                        key={appt.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="hover:bg-[var(--interactive-hover)] transition-all text-sm"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="text-content font-bold text-xs">
                                                {new Date(appt.appointment_date + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 shrink-0">
                                                    {appt.crm_company_name ? <Building2 size={14} /> : <User size={14} />}
                                                </div>
                                                <span className="font-bold text-content">{contactLabel(appt)}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-[var(--text-muted)] text-xs">{appt.prestation_label || '—'}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-[var(--text-muted)] text-xs">
                                                <Clock size={12} />
                                                {appt.start_time || '--:--'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {!assigned ? (
                                                    <span className="text-content-muted italic text-xs">Aucun</span>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded bg-green-500/10 text-green-600 dark:text-green-400 text-[10px] font-bold">
                                                        {assigned}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </motion.tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const EvenementielUpcomingTable = () => {
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        setLoading(true);
        try {
            const data = await moduleApi.getEvenementiel();
            setEvents(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const filteredEvents = events
        .filter(event => {
            const name = event.type === 'PRIVÉ' ? `${event.first_name || ''} ${event.last_name || ''}` : (event.company_name || '');
            const isMatch = name.toLowerCase().includes(searchTerm.toLowerCase());

            if (!event.start_time) return false;

            // On considère l'événement comme "à venir" s'il n'est pas encore terminé
            const now = new Date();
            const eventEnd = new Date(event.end_time || event.start_time);

            return isMatch && eventEnd.getTime() >= now.getTime();
        })
        .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* « Événements à venir » apparaissait DEUX FOIS sur le tableau de bord (signalé par Emeni) :
                    ici, et dans la colonne de droite. Les deux blocs ne montrent pourtant pas la même
                    chose — celui de droite liste les quatre prochains événements, celui-ci est un tableau
                    du PERSONNEL par privatisation (Pax, horaires, hôtesse interne, extras). C'est le titre
                    qui était faux, pas le bloc : on le nomme pour ce qu'il est. */}
                <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">Privatisations à venir — hôtesses et extras</div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted pointer-events-none" size={15} />
                    <input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="field-input w-full sm:w-64"
                        style={{ paddingLeft: 34 }}
                    />
                </div>
            </div>

            <div className="bg-surface border border-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-surface-soft border-b border-border">
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Date</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Nom / Entreprise</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em] text-center">Pax</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Horaires</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em]">Hôtesse Interne</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-content-muted uppercase tracking-[.09em] text-center">Extras Hôtesses</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-[var(--text-muted)]">Chargement...</td>
                                </tr>
                            ) : filteredEvents.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-[var(--text-muted)]">Aucune privatisation trouvée</td>
                                </tr>
                            ) : filteredEvents.map((event) => {
                                const clientName = event.type === 'PRIVÉ'
                                    ? `${event.first_name || ''} ${event.last_name || ''}`.trim()
                                    : (event.company_name || '—');

                                const hotesseStaff = event.staff?.find((s: any) => s.name?.toLowerCase().includes('hôtesse'));
                                const hotesseAssignments = event.assignments?.filter((a: any) => a.staff_type_name?.toLowerCase().includes('hôtesse')) || [];

                                const needed = hotesseStaff?.count || 0;
                                const assigned = hotesseAssignments.length;
                                const extras = Math.max(0, needed - assigned);

                                const startTime = event.start_time
                                    ? new Date(event.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                                    : '--:--';
                                const endTime = event.end_time
                                    ? new Date(event.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                                    : '--:--';

                                return (
                                    <motion.tr
                                        key={event.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="hover:bg-[var(--interactive-hover)] transition-all text-sm"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="text-content font-bold text-xs">
                                                {event.start_time ? new Date(event.start_time).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 shrink-0">
                                                    {event.type === 'PRIVÉ' ? <User size={14} /> : <Building2 size={14} />}
                                                </div>
                                                <span className="font-bold text-content">{clientName}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-surface-soft text-content-muted border border-border text-xs font-bold">
                                                <Users size={12} />
                                                {event.num_people || 0}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-[var(--text-muted)] text-xs">
                                                <Clock size={12} />
                                                {startTime} → {endTime}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {hotesseAssignments.length === 0 ? (
                                                    <span className="text-content-muted italic text-xs">Aucune</span>
                                                ) : hotesseAssignments.map((a: any, i: number) => (
                                                    <span key={i} className="px-2 py-0.5 rounded bg-green-500/10 text-green-600 dark:text-green-400 text-[10px] font-bold">
                                                        {a.employee_name}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {extras > 0 ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-bold border border-orange-500/20">
                                                    <AlertCircle size={12} />
                                                    {extras}
                                                </span>
                                            ) : (
                                                <span className="text-content-muted text-xs">—</span>
                                            )}
                                        </td>
                                    </motion.tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export const PlanningTableView = () => {
    const { user } = useAuth();
    const isPrestationClient = (user as any)?.client_type === 'prestation';
    return isPrestationClient ? <PrestationUpcomingTable /> : <EvenementielUpcomingTable />;
};
